import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"

export interface Organization {
  id: string
  internal_id: string
  name: string
  type: "group--partner" | "group--organization"
  uuid: { value: string }[]
}

export type UserData = {
  user: {
    field_first_name?: { value: string }[]
    field_last_name?: { value: string }[]
    mail?: { value: string }[]
    roles: string[]
    uuid?: { value: string }[]
    uid?: { value: string }[]
  }
  cross_org: Organization[]
  active_org: Organization | null
  org_roles?: Record<string, unknown> | null
}

export interface User {
  id: string
  userId: number
  email: string | null
  firstName: string | null
  lastName: string | null
  roles: string[]
  cross_org: Organization[]
  active_org: Organization | null
  activeOrgRoles: string[] | null
}

export interface SessionState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  maintenanceMode: boolean
}

const initialState: SessionState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  maintenanceMode: false,
}

const ORGANIZATION_SERVICE_ADMIN_ROLE = "organization-service_admin"

// Async thunk to check if user is logged in
export const checkAuth = createAsyncThunk<User | null>(
  "session/checkAuth",
  async (_, { rejectWithValue }) => {
    try {
      // Use the full API URL to check session (not through proxy)
      const apiUrl = import.meta.env.VITE_API_URL || ""
      const response = await fetch(`${apiUrl}/api/user/user/me`, {
        credentials: "include",
      })

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          return null // User is not logged in
        }
        const data = await response.json().catch(() => ({}))
        if (data.maintenance) {
          throw new Error("maintenance")
        }
        throw new Error("Failed to fetch user data")
      }
      // API returns an array or object with user data
      const data = await response.json()
      const {
        user: rawUser,
        cross_org,
        active_org,
        org_roles: rawOrgRoles,
      } = data as UserData

      if (rawUser) {
        const userId = Number(rawUser.uid?.[0]?.value)
        const id = String(rawUser.uuid?.[0]?.value)
        const activeOrgRoles = rawOrgRoles ? Object.keys(rawOrgRoles) : null
        const user: User = {
          id,
          userId,
          email: rawUser.mail?.[0]?.value || null,
          firstName: rawUser.field_first_name?.[0]?.value || null,
          lastName: rawUser.field_last_name?.[0]?.value || null,
          roles: rawUser.roles || [],
          cross_org,
          active_org,
          activeOrgRoles,
        }
        return user
      }

      console.warn("checkAuth: Failed to parse user data, returning null")
      return null
    } catch (error) {
      if (error instanceof Error && error.message === "maintenance") {
        return rejectWithValue("maintenance")
      }
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      )
    }
  }
)

// Async thunk to logout
export const logout = createAsyncThunk("session/logout", async () => {
  const apiUrl = import.meta.env.VITE_API_URL || ""
  await fetch(`${apiUrl}/api/user/user/logout`, {
    credentials: "include",
  })
})

const sessionSlice = createSlice({
  name: "session",
  initialState,
  reducers: {
    setMaintenanceMode: (state, action) => {
      state.maintenanceMode = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkAuth.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload
        state.isAuthenticated = action.payload !== null
      })
      .addCase(checkAuth.rejected, (state, action) => {
        state.isLoading = false
        state.isAuthenticated = false
        state.user = null
        if (action.payload === "maintenance") {
          state.maintenanceMode = true
        } else {
          state.error = action.payload as string
        }
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null
        state.isAuthenticated = false
      })
  },
})

export const { setMaintenanceMode, clearError } = sessionSlice.actions
export default sessionSlice.reducer

// Selectors
export const selectUser = (state: { session: SessionState }) =>
  state.session.user
export const selectUserIsOrgServiceAdmin = (state: { session: SessionState }) =>
  state.session.user?.activeOrgRoles?.includes(
    ORGANIZATION_SERVICE_ADMIN_ROLE
  ) ?? false
export const selectOrganizations = (state: { session: SessionState }) =>
  state.session.user?.cross_org ?? []
export const selectIsAuthenticated = (state: { session: SessionState }) =>
  state.session.isAuthenticated
export const selectIsLoading = (state: { session: SessionState }) =>
  state.session.isLoading
export const selectMaintenanceMode = (state: { session: SessionState }) =>
  state.session.maintenanceMode
export const selectSessionError = (state: { session: SessionState }) =>
  state.session.error

export const selectActiveOrgId = (state: { session: SessionState }) => {
  const activeOrgId = state.session.user?.active_org?.uuid[0]?.value
  if (!activeOrgId) {
    throw new Error("Unable to get active organization ID from session state")
  }

  return activeOrgId
}

export const selectActiveOrg = (state: { session: SessionState }) => {
  const organizations = selectOrganizations(state)
  const activeOrgId = state.session.user?.active_org?.uuid[0]?.value

  if (!activeOrgId) {
    return organizations[0] ?? null
  }

  return (
    organizations.find(
      (organization) =>
        organization.id === activeOrgId ||
        organization.internal_id === activeOrgId
    ) ??
    organizations[0] ??
    null
  )
}
